/**
 * Shared regex and utilities for `[[type:uuid]]` entity reference tokens
 * emitted by the Phoebe agent. The chat view resolves these into interactive
 * badges; other surfaces (notification bell, etc.) strip them to plain text.
 */

const UUID_SOURCE =
    '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * Matches `[[type:uuid]]` tokens produced by the Phoebe agent. The id must
 * be a full UUID (optionally `uuid:uuid` for conversation message refs),
 * mirroring the backend parser in
 * `libraries/python/entity_references/grammar.py` so both ends accept
 * the same grammar — malformed tokens render as literal text.
 */
export const ENTITY_REFERENCE_PATTERN = new RegExp(
    `\\[\\[([A-Za-z_]+):(${UUID_SOURCE}(?::${UUID_SOURCE})?)` +
        '(?:\\|([^\\]|\\n]{1,80}))?\\]\\]',
    'g',
);

/**
 * Matches a well-formed token wrapped in a second pair of brackets:
 * `[[[[shift:uuid]]]]`. Built from the same source as
 * `ENTITY_REFERENCE_PATTERN` so the two cannot drift.
 */
const NESTED_ENTITY_REFERENCE_PATTERN = new RegExp(
    `\\[\\[(\\[\\[[A-Za-z_]+:${UUID_SOURCE}(?::${UUID_SOURCE})?\\]\\])\\]\\]`,
    'g',
);

/**
 * Collapse `[[[[type:uuid]]]]` down to `[[type:uuid]]`.
 *
 * Messages persisted before the backend stopped nesting tokens still carry
 * this shape: the consumers below match only the inner token, so the outer
 * delimiters survive as literal `[[` / `]]` around the resolved badge or
 * name. Only brackets flanking a well-formed token are removed, so a bare
 * `[[…]]` span the model wrote for its own reasons is left alone.
 */
export function unwrapNestedEntityReferences(text: string): string {
    return text.replace(NESTED_ENTITY_REFERENCE_PATTERN, '$1');
}

const ENTITY_LABELS: Record<string, string> = {
    caregiver: 'a caregiver',
    client: 'a client',
    shift: 'a shift',
    callout: 'an outreach',
    group: 'a shift group',
    conversation: 'a conversation',
};

const ENTITY_REFERENCE_MARKDOWN_HREF_PREFIX = '#entity-reference:';

type EntityReferenceDisplay = {
    readonly id: string;
    readonly display_name: string;
};

export type MarkdownEntityReference = {
    readonly type: string;
    readonly id: string;
};

/**
 * The label a reference falls back to when nothing resolved it. Written to
 * read naturally inside a sentence: "find coverage for a client".
 */
export function entityReferenceFallbackLabel(type: string): string {
    const normalizedType = type.toLowerCase();
    return ENTITY_LABELS[normalizedType] ?? `a ${normalizedType}`;
}

/**
 * Replace `[[type:uuid]]` tokens with a plain-text label so the text
 * reads naturally without raw UUIDs. Falls back to "a <type>" for
 * unknown entity types.
 */
export function stripEntityReferences(text: string): string {
    return unwrapNestedEntityReferences(text).replace(
        ENTITY_REFERENCE_PATTERN,
        // A source citation wraps the agent's own words; they read
        // better here than the generic label.
        (_match, type: string, _id: string, phrase?: string) =>
            phrase?.trim() || entityReferenceFallbackLabel(type),
    );
}

/**
 * Replace `[[type:uuid]]` tokens with the resolved display name from
 * a references array. Used when copying message text to the clipboard
 * so pasted content shows human-readable names instead of raw UUIDs.
 * Falls back to the generic label when no reference data is available.
 */
export function resolveEntityReferences(
    text: string,
    references: ReadonlyArray<EntityReferenceDisplay> | undefined,
): string {
    if (!references || references.length === 0) {
        return stripEntityReferences(text);
    }
    const refMap = new Map(
        references.map((r) => [r.id.toLowerCase(), r.display_name]),
    );
    return unwrapNestedEntityReferences(text).replace(
        ENTITY_REFERENCE_PATTERN,
        (_match, type: string, id: string, phrase?: string) => {
            const wrapped = phrase?.trim();
            if (wrapped) {
                return wrapped;
            }
            const name = refMap.get(id.toLowerCase());
            return name || entityReferenceFallbackLabel(type);
        },
    );
}

export function linkEntityReferencesForMarkdown(text: string): string {
    return unwrapNestedEntityReferences(text).replace(
        ENTITY_REFERENCE_PATTERN,
        (_match, type: string, id: string) =>
            `[reference](${ENTITY_REFERENCE_MARKDOWN_HREF_PREFIX}${type.toLowerCase()}:${id})`,
    );
}

/**
 * Drops an entity reference that is still arriving.
 *
 * A reference is written `[[caregiver:<uuid>]]`, so a message that stops
 * partway through one shows the reader a raw bracketed UUID until the closing
 * brackets land. Only use this on text that is still streaming.
 */
export function hideIncompleteEntityReference(text: string): string {
    const opened = text.lastIndexOf('[[');
    return opened > text.lastIndexOf(']]') ? text.slice(0, opened) : text;
}

/** Split a `#entity-reference:<type>:<id>` Markdown href back apart. */
export function parseMarkdownEntityReferenceHref(
    href: string | undefined,
): MarkdownEntityReference | null {
    if (!href?.startsWith(ENTITY_REFERENCE_MARKDOWN_HREF_PREFIX)) {
        return null;
    }
    const [type = '', ...idParts] = href
        .slice(ENTITY_REFERENCE_MARKDOWN_HREF_PREFIX.length)
        .split(':');
    return { type: type.toLowerCase(), id: idParts.join(':') };
}
